import React, { useState, useEffect, useContext, createContext } from "react";
import { useKeycloak } from "@react-keycloak/web";
import Axios from "axios";
import GYAxios from "./GYAxios";

const siteContext = createContext();

export function ProvideSite({ children }) {
	const { keycloak, initialized } = useKeycloak();
	const site = useProvideSite({ keycloak, initialized });
	return <siteContext.Provider value={site}>{children}</siteContext.Provider>;
}

export const useSite = () => {
	return useContext(siteContext);
};

function useProvideSite(keycloakObj) {
	const keycloak = keycloakObj.keycloak;
	const defaultSelect = { label: "Select a site..", value: "all" };
	const [selectedSite, setSelectedSite] = useState(defaultSelect);
	const [allSites_Select, setAllSites_Select] = useState([]);
	const [showSelect, setShowSelect] = useState(false);
	const [authorisedSites, setAuthorisedSites] = useState([]);
	const [sitesRetrieved, setSitesRetrieved] = useState(false);
	const [tempKey, setTempKey] = useState("");

	/**
	 * Get single site info
	 * @param {Site ID} siteID
	 * @returns Site object
	 */
	const getSingleSite = async (siteID) => {
		let res = null;

		try {
			res = await GYAxios.get(`/site/filter/site_id/${siteID}`);
			const resData = res.data[0];
			return {
				label: resData.site_name,
				value: resData,
			};
		} catch (error) {
			console.log("error getSingleSite:", error);
		}
	};

	/**
	 * Change selected site
	 * @param {Selected site} site
	 */
	const changeSite = (site) => {
		switch (typeof site) {
			case "object":
				setSelectedSite(site);

				if (
					site.label === defaultSelect.label ||
					typeof site.label == "undefined"
				)
					sessionStorage.removeItem("site_id");
				else sessionStorage.setItem("site_id", site.value.site_id);
				break;

			default:
				const siteNumber = parseInt(site);
				if (!isNaN(siteNumber)) {
					getSingleSite(siteNumber).then((resSS) => {
						setSelectedSite(resSS);
					});
				}
				break;
		}
	};

	/**
	 * Clear sites and return back to default state
	 */
	const clearSites = () => {
		changeSite(defaultSelect);
		setAllSites_Select([]);

		sessionStorage.removeItem("site_id");
		sessionStorage.removeItem("company");
	};

	/**
	 * Show site select bar
	 * @param {show status} show
	 */
	const showSelectSites = (show) => {
		setShowSelect(show);
	};

	useEffect(() => {
		if(keycloak.token && tempKey !== keycloak.token)
			setTempKey(keycloak.token);
	}, [keycloak.token, tempKey]);

	useEffect(() => {
		const cookieIsSet = tempKey !== "";

		if (keycloakObj.initialized && cookieIsSet) {
			const userToken = keycloakObj.keycloak.tokenParsed;

			if (userToken != null && userToken.roles != null) {
				const keycloakID = userToken.sub;
				let userType = "";
				let column = "";
				let id = "";

				GYAxios.get(`/users/${keycloakID}/init/signin`)
					.then((res) => {
						const userInfo =
							res.data.error == null && res.data.length > 0
								? res.data[0]
								: false;

						if (userInfo) {
							if (userToken.roles.includes("_GY:Base")) {
								userType = "admin";
								column = "all";
								id = "all";
							} else if (
								userInfo.client_id != null &&
								(userInfo.site_id == null || userInfo.site_id == "all")
							) {
								userType = "client";
								column = "client_id";
								id = userInfo.client_id;
							} else if (userToken.roles.includes("_CLIENT:Multi")) {
								userType = "client_multi";
								column = "site_id";
								id = userInfo.site_id;
							} else if (userToken.roles.includes("_CLIENT:Base")) {
								userType = "client_site";
								column = "site_id";
								id = userInfo.site_id;
							}

							const authSitesURI =
								userType === "admin"
									? "/site/"
									: `/site/filter/${column}/${id}`;
							const authSitesReq = GYAxios.get(authSitesURI);
							const mainSelectURI = `/select/main/sites/${userType}/filter/${column}/${id}`;

							const mainSelectReq = GYAxios.get(mainSelectURI);
							const reqArray = [authSitesReq, mainSelectReq];

							Axios.all(reqArray).then(
								Axios.spread((...responses) => {
									const authSitesRes =
										responses[0].data.error == null
											? responses[0].data.map((site) => site.site_id)
											: [];
									const mainSelectRes =
										responses[1].data.error == null ? responses[1].data : [];
									// Set authorized site for logged in user
									setAuthorisedSites(authSitesRes);
									// Show Site Select for Admin and Client user
									if (userType !== "client_site") {
										mainSelectRes.forEach((client) => {
											const sites = client.options;
											for (let i = 0; i < sites.length; i++) {
												if (
													userType !== "admin" &&
													sites[i].value.operation_status === 1
												) {
													sites.splice(i, 1);
												}

												switch (sites[i].value.operation_status) {
													case 1:
														sites[i].label += " (LIP)";
														break;
													case 2:
														sites[i].label += " (MSA)";
														break;
													case 4:
														sites[i].label += " (TEST)";
														break;
												}
											}
											// remove group label if there is only one site
											if(client.options?.[0]?.value?.is_solo) { client.label = '' }
										});

										setAllSites_Select(mainSelectRes);
										setShowSelect(true);
									}
									// Set selected site and hide site select
									else {
										const tempSite = mainSelectRes[0].options[0];
										const resObj = {
											label: tempSite.site_name,
											value: tempSite,
										};
										setSelectedSite(resObj);
									}

									setSitesRetrieved(true);
								})
							);
						}
					})
					.catch((error) => console.log(error));
			}
		}
	}, [keycloakObj.initialized, tempKey]);

	useEffect(() => {
		const siteID_ss =
			sessionStorage.getItem("site_id") != null
				? sessionStorage.getItem("site_id")
				: null;

		if (siteID_ss != null) changeSite(siteID_ss);
	}, [sitesRetrieved]);

	return {
		selectedSite,
		authorisedSites,
		showSelect,
		allSites_Select,
		showSelectSites,
		defaultSelect,
		changeSite,
		clearSites,
	};
}
